/**
 * Quran Service
 * Full API service using @quranjs/api SDK (Quran Foundation)
 * Covers: Chapters, Verses, Audio, Resources APIs
 */

import quranClient from '@/lib/quranClient';
import type { ChapterId, VerseKey, PageNumber, JuzNumber, HizbNumber, RubNumber } from '@quranjs/api';
import type {
  Surah,
  SurahListItem,
  Ayah,
  QuranWord,
  ChapterInfo,
  ReadingProgress,
  ReaderSettings,
  Reciter,
  TranslationText,
  TafsirText,
  TranslationSource,
  TafsirSource,
  LanguageInfo,
} from '@/types/quran';

// ─── Storage Keys ────────────────────────────────────────
const STORAGE_KEY_PROGRESS = 'quran-reading-progress';
const STORAGE_KEY_SETTINGS = 'quran-reader-settings';
const STORAGE_KEY_CACHE = 'quran-surah-cache';

// ─── Cache Management ────────────────────────────────────
interface CacheData {
  surahs?: SurahListItem[];
  surahData: { [key: number]: Surah };
  timestamp: number;
}

const CACHE_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

function getCache(): CacheData {
  try {
    const cached = localStorage.getItem(STORAGE_KEY_CACHE);
    if (cached) {
      const data: CacheData = JSON.parse(cached);
      if (Date.now() - data.timestamp < CACHE_DURATION) {
        return data;
      }
    }
  } catch (error) {
    console.error('Error reading cache:', error);
  }
  return { surahData: {}, timestamp: Date.now() };
}

function updateCache(data: Partial<CacheData>) {
  try {
    const current = getCache();
    const updated: CacheData = { ...current, ...data, timestamp: Date.now() };
    localStorage.setItem(STORAGE_KEY_CACHE, JSON.stringify(updated));
  } catch (error) {
    console.error('Error updating cache:', error);
  }
}

// ─── Helper: Map SDK Verse → Ayah ────────────────────────
function mapVerse(v: any, fallbackPage?: number): Ayah {
  // Extract chapterId — API may not include it, so fall back to parsing verseKey (e.g. "18:62")
  let surahNumber = 0;
  if (v.chapterId != null) {
    surahNumber = typeof v.chapterId === 'string' ? parseInt(v.chapterId, 10) : v.chapterId;
  } else if (v.chapter_id != null) {
    surahNumber = typeof v.chapter_id === 'string' ? parseInt(v.chapter_id, 10) : v.chapter_id;
  } else if (v.verseKey) {
    surahNumber = parseInt(String(v.verseKey).split(':')[0], 10) || 0;
  } else if (v.verse_key) {
    surahNumber = parseInt(String(v.verse_key).split(':')[0], 10) || 0;
  }

  return {
    number: v.id ?? 0,
    numberInSurah: v.verseNumber ?? v.verse_number ?? 0,
    surahNumber,
    text: v.textUthmani ?? v.text_uthmani ?? '',
    textTajweed: v.textUthmaniTajweed ?? v.text_uthmani_tajweed ?? undefined,
    juz: v.juzNumber ?? v.juz_number ?? 0,
    manzil: 0,
    page: v.pageNumber ?? v.page_number ?? fallbackPage ?? 0,
    ruku: 0,
    hizbQuarter: v.rubElHizbNumber ?? v.rub_el_hizb_number ?? 0,
    sajda: false,
    translations: v.translations?.map((t: any) => ({
      id: t.id,
      text: t.text,
      resourceId: t.resourceId ?? t.resource_id,
      resourceName: t.resourceName ?? t.resource_name,
      languageName: t.languageName ?? t.language_name,
    })) as TranslationText[] | undefined,
    tafsirs: v.tafsirs?.map((t: any) => ({
      id: t.id,
      text: t.text,
      resourceId: t.resourceId ?? t.resource_id,
      resourceName: t.resourceName ?? t.resource_name,
      languageName: t.languageName ?? t.language_name,
    })) as TafsirText[] | undefined,
    words: v.words?.map((w: any) => ({
      id: w.id,
      position: w.position ?? 0,
      charTypeName: w.charTypeName ?? w.char_type_name ?? 'word',
      codeV2: w.codeV2 ?? w.code_v2 ?? w.text ?? '',
      textQpcHafs: w.textQpcHafs ?? w.text_qpc_hafs ?? w.textUthmani ?? w.text_uthmani ?? w.text ?? '',
      pageNumber: w.pageNumber ?? w.page_number ?? v.pageNumber ?? v.page_number ?? fallbackPage,
      lineNumber: w.lineNumber ?? w.line_number,
      audioUrl: w.audioUrl ?? w.audio_url,
      verseKey: w.verseKey ?? w.verse_key ?? v.verseKey ?? v.verse_key,
      translation: w.translation,
      transliteration: w.transliteration,
    })) as QuranWord[] | undefined,
  };
}

// ═══════════════════════════════════════════════════════════
// CHAPTERS API
// ═══════════════════════════════════════════════════════════

/** Get list of all Surahs */
export async function getSurahs(): Promise<SurahListItem[]> {
  try {
    const cache = getCache();
    if (Array.isArray(cache.surahs) && cache.surahs.length > 0) return cache.surahs;

    const chapters = await quranClient.chapters.findAll();
    const surahs: SurahListItem[] = chapters.map((ch) => ({
      number: ch.id,
      name: ch.nameArabic,
      englishName: ch.nameSimple,
      englishNameTranslation: ch.translatedName?.name ?? ch.nameSimple,
      numberOfAyahs: ch.versesCount,
      revelationType: ch.revelationPlace === 'medinan' ? 'Medinan' : 'Meccan',
    }));
    updateCache({ surahs });
    return surahs;
  } catch (error) {
    console.error('Error fetching surahs:', error);
    throw new Error('Failed to load Quran chapters');
  }
}

/** Get a specific Surah with all Ayahs */
export async function getSurah(surahNumber: number): Promise<Surah> {
  try {
    const cache = getCache();
    if (cache.surahData[surahNumber]) return cache.surahData[surahNumber];

    const chapter = await quranClient.chapters.findById(String(surahNumber) as ChapterId);
    const allAyahs: Ayah[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const result = await quranClient.verses.findByChapter(String(surahNumber) as ChapterId, {
        perPage: 50,
        page,
        mushaf: 2,
        fields: { textUthmani: true, textUthmaniTajweed: true },
      });
      const verses = Array.isArray(result) ? result : (result as any).verses ?? [];
      for (const v of verses) allAyahs.push(mapVerse(v));

      const pagination = (result as any)?.pagination;
      if (pagination && pagination.currentPage < pagination.totalPages) page++;
      else if (verses.length === 50) page++;
      else hasMore = false;
    }

    const surah: Surah = {
      number: chapter.id,
      name: chapter.nameArabic,
      englishName: chapter.nameSimple,
      englishNameTranslation: chapter.translatedName?.name ?? chapter.nameSimple,
      numberOfAyahs: chapter.versesCount,
      revelationType: chapter.revelationPlace === 'medinan' ? 'Medinan' : 'Meccan',
      ayahs: allAyahs,
    };
    const updatedSurahData = { ...cache.surahData, [surahNumber]: surah };
    updateCache({ surahData: updatedSurahData });
    return surah;
  } catch (error) {
    console.error(`Error fetching surah ${surahNumber}:`, error);
    throw new Error(`Failed to load Surah ${surahNumber}`);
  }
}

/** Get chapter info (description, source) */
export async function getChapterInfo(surahNumber: number): Promise<ChapterInfo> {
  try {
    const info = await quranClient.chapters.findInfoById(String(surahNumber) as ChapterId);
    return {
      id: info.id,
      chapterId: info.chapterId,
      text: info.text,
      shortText: info.shortText,
      source: info.source,
    };
  } catch (error) {
    console.error(`Error fetching chapter info ${surahNumber}:`, error);
    throw new Error(`Failed to load chapter info for Surah ${surahNumber}`);
  }
}

/** Get the Mushaf page where a surah starts */
export async function getFirstPageOfSurah(surahNumber: number): Promise<number> {
  try {
    const chapter = await quranClient.chapters.findById(String(surahNumber) as ChapterId);
    if (chapter.pages && chapter.pages.length > 0) return chapter.pages[0];
    return 1;
  } catch (error) {
    console.error(`Error getting first page of surah ${surahNumber}:`, error);
    return 1;
  }
}

// ═══════════════════════════════════════════════════════════
// VERSES API
// ═══════════════════════════════════════════════════════════

/** Get a specific Ayah */
export async function getAyah(surahNumber: number, ayahNumber: number): Promise<Ayah> {
  try {
    const verse = await quranClient.verses.findByKey(`${surahNumber}:${ayahNumber}` as VerseKey, {
      fields: { textUthmani: true, textUthmaniTajweed: true },
    });
    return mapVerse(verse);
  } catch (error) {
    console.error(`Error fetching ayah ${surahNumber}:${ayahNumber}:`, error);
    throw new Error(`Failed to load Ayah ${surahNumber}:${ayahNumber}`);
  }
}

/** Get single Ayah with word-level data for QCF rendering */
export async function getAyahWithWords(surahNumber: number, ayahNumber: number): Promise<Ayah> {
  try {
    const verse = await quranClient.verses.findByKey(`${surahNumber}:${ayahNumber}` as VerseKey, {
      words: true,
      wordFields: { codeV2: true },
    });
    return mapVerse(verse);
  } catch (error) {
    console.error(`Error fetching ayah with words ${surahNumber}:${ayahNumber}:`, error);
    throw new Error(`Failed to load Ayah ${surahNumber}:${ayahNumber}`);
  }
}

/** Get Ayahs for a specific Mushaf page (1-604) — includes word-level data for QCF rendering */
export async function getAyahsByPage(
  pageNumber: number,
  options?: { translations?: number[]; tafsirs?: number[] }
): Promise<Ayah[]> {
  try {
    const allAyahs: Ayah[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const result = await quranClient.verses.findByPage(String(pageNumber) as PageNumber, {
        perPage: 50,
        page,
        fields: { textUthmani: true, textUthmaniTajweed: true },
        words: true,
        wordFields: { codeV2: true },
        ...(options?.translations && { translations: options.translations }),
        ...(options?.tafsirs && { tafsirs: options.tafsirs }),
      });
      
      const verses = Array.isArray(result) ? result : (result as any).verses ?? [];
      for (const v of verses) {
        allAyahs.push(mapVerse(v, pageNumber));
      }

      const pagination = (result as any)?.pagination;
      if (pagination && pagination.currentPage < pagination.totalPages) {
        page++;
      } else if (verses.length === 50) {
        page++;
      } else {
        hasMore = false;
      }
    }
    
    return allAyahs;
  } catch (error) {
    console.error(`Error fetching page ${pageNumber}:`, error);
    throw new Error(`Failed to load page ${pageNumber}`);
  }
}

/** Get Ayahs by Juz (1–30) */
export async function getAyahsByJuz(juzNumber: number): Promise<Ayah[]> {
  try {
    const allAyahs: Ayah[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const result = await quranClient.verses.findByJuz(String(juzNumber) as JuzNumber, {
        perPage: 50,
        page,
        fields: { textUthmani: true, textUthmaniTajweed: true },
      });
      const verses = Array.isArray(result) ? result : (result as any).verses ?? [];
      for (const v of verses) allAyahs.push(mapVerse(v));

      const pagination = (result as any)?.pagination;
      if (pagination && pagination.currentPage < pagination.totalPages) {
        page++;
      } else if (verses.length === 50) {
        page++;
      } else {
        hasMore = false;
      }
    }
    return allAyahs;
  } catch (error) {
    console.error(`Error fetching juz ${juzNumber}:`, error);
    throw new Error(`Failed to load Juz ${juzNumber}`);
  }
}

/** Get Ayahs by Hizb (1–60) */
export async function getAyahsByHizb(hizbNumber: number): Promise<Ayah[]> {
  try {
    const allAyahs: Ayah[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const result = await quranClient.verses.findByHizb(String(hizbNumber) as HizbNumber, {
        perPage: 50,
        page,
        fields: { textUthmani: true, textUthmaniTajweed: true },
      });
      const verses = Array.isArray(result) ? result : (result as any).verses ?? [];
      for (const v of verses) allAyahs.push(mapVerse(v));

      const pagination = (result as any)?.pagination;
      if (pagination && pagination.currentPage < pagination.totalPages) {
        page++;
      } else if (verses.length === 50) {
        page++;
      } else {
        hasMore = false;
      }
    }
    return allAyahs;
  } catch (error) {
    console.error(`Error fetching hizb ${hizbNumber}:`, error);
    throw new Error(`Failed to load Hizb ${hizbNumber}`);
  }
}

/** Get Ayahs by Rub El Hizb (1–240) */
export async function getAyahsByRub(rubNumber: number): Promise<Ayah[]> {
  try {
    const allAyahs: Ayah[] = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      const result = await quranClient.verses.findByRub(String(rubNumber) as RubNumber, {
        perPage: 50,
        page,
        fields: { textUthmani: true, textUthmaniTajweed: true },
      });
      const verses = Array.isArray(result) ? result : (result as any).verses ?? [];
      for (const v of verses) allAyahs.push(mapVerse(v));

      const pagination = (result as any)?.pagination;
      if (pagination && pagination.currentPage < pagination.totalPages) {
        page++;
      } else if (verses.length === 50) {
        page++;
      } else {
        hasMore = false;
      }
    }
    return allAyahs;
  } catch (error) {
    console.error(`Error fetching rub ${rubNumber}:`, error);
    throw new Error(`Failed to load Rub ${rubNumber}`);
  }
}

/** Get a random verse */
export async function getRandomVerse(translationIds?: number[]): Promise<Ayah> {
  try {
    const verse = await quranClient.verses.findRandom({
      fields: { textUthmani: true, textUthmaniTajweed: true },
      ...(translationIds && { translations: translationIds }),
    });
    return mapVerse(verse);
  } catch (error) {
    console.error('Error fetching random verse:', error);
    throw new Error('Failed to load random verse');
  }
}

/** Get Ayah with specific translations */
export async function getAyahWithTranslations(
  surahNumber: number,
  ayahNumber: number,
  translationIds: number[]
): Promise<Ayah> {
  try {
    const verse = await quranClient.verses.findByKey(`${surahNumber}:${ayahNumber}` as VerseKey, {
      fields: { textUthmani: true, textUthmaniTajweed: true },
      translations: translationIds,
    });
    return mapVerse(verse);
  } catch (error) {
    console.error(`Error fetching ayah translations ${surahNumber}:${ayahNumber}:`, error);
    throw new Error(`Failed to load ayah translations for ${surahNumber}:${ayahNumber}`);
  }
}

/** Get Ayah with tafsir */
export async function getAyahWithTafsir(
  surahNumber: number,
  ayahNumber: number,
  tafsirIds: number[]
): Promise<Ayah> {
  try {
    const verse = await quranClient.verses.findByKey(`${surahNumber}:${ayahNumber}` as VerseKey, {
      fields: { textUthmani: true, textUthmaniTajweed: true },
      tafsirs: tafsirIds,
    });
    return mapVerse(verse);
  } catch (error) {
    console.error(`Error fetching tafsir ${surahNumber}:${ayahNumber}:`, error);
    throw new Error(`Failed to load tafsir for ${surahNumber}:${ayahNumber}`);
  }
}


// ═══════════════════════════════════════════════════════════
// AUDIO API
// ═══════════════════════════════════════════════════════════

/** แปลง URL จาก API ให้ใช้เล่นได้ (protocol-relative, relative path) */
function normalizeAudioUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('//')) return `https:${url}`;
  if (url.startsWith('http')) return url;
  return `https://verses.quran.foundation/${url}`;
}

/** Get audio URL for a specific verse */
export async function getVerseAudio(
  surahNumber: number,
  ayahNumber: number,
  reciterId: string = '7'
): Promise<string | null> {
  try {
    const { audioFiles } = await quranClient.audio.findVerseRecitationsByKey(
      `${surahNumber}:${ayahNumber}` as VerseKey,
      reciterId,
    );
    if (audioFiles && audioFiles.length > 0) {
      const url = audioFiles[0].url;
      if (!url) return null;
      return normalizeAudioUrl(url);
    }
    return null;
  } catch (error) {
    console.error(`Error fetching audio ${surahNumber}:${ayahNumber}:`, error);
    return null;
  }
}

/**
 * ดึง URL สำหรับเล่นตัวอย่างเสียงกอรี (อายะห์ 1:1 หรือต้นซูเราะห์อัลฟาติฮะห์)
 * ลองเสียงอายะห์ต่ออายะห์ก่อน ถ้าไม่มีจะลองเสียงทั้งบท
 */
export async function getPreviewAudioUrl(reciterId: string): Promise<string | null> {
  const verseUrl = await getVerseAudio(1, 1, reciterId);
  if (verseUrl) return verseUrl;
  const chapterUrl = await getSurahAudio(1, reciterId);
  if (chapterUrl) return normalizeAudioUrl(chapterUrl);
  return null;
}

/** Get audio URL for full surah */
export async function getSurahAudio(
  surahNumber: number,
  reciterId: string = '7'
): Promise<string | null> {
  try {
    const audio = await quranClient.audio.findChapterRecitationById(
      String(reciterId),
      String(surahNumber) as ChapterId,
    );
    const url = audio?.audioUrl ?? null;
    if (url) return normalizeAudioUrl(url);
    return null;
  } catch (error) {
    console.error(`Error fetching surah audio ${surahNumber}:`, error);
    return null;
  }
}

/** Get all chapter recitations for a reciter (all 114 surahs) */
export async function getAllChapterRecitations(
  reciterId: string = '7'
): Promise<{ chapterId: number; audioUrl: string; fileSize: number }[]> {
  try {
    const recitations = await quranClient.audio.findAllChapterRecitations(reciterId);
    return recitations.map((r) => ({
      chapterId: r.chapterId,
      audioUrl: r.audioUrl,
      fileSize: r.fileSize,
    }));
  } catch (error) {
    console.error('Error fetching all chapter recitations:', error);
    return [];
  }
}

/** Get all verse recitations for a chapter */
export async function getVerseRecitationsByChapter(
  surahNumber: number,
  reciterId: string = '7'
): Promise<{ verseKey: string; url: string }[]> {
  try {
    const { audioFiles } = await quranClient.audio.findVerseRecitationsByChapter(
      String(surahNumber) as ChapterId,
      reciterId,
    );
    return audioFiles.map((a) => ({ verseKey: a.verseKey, url: a.url }));
  } catch (error) {
    console.error(`Error fetching verse recitations for chapter ${surahNumber}:`, error);
    return [];
  }
}

// ═══════════════════════════════════════════════════════════
// RESOURCES API
// ═══════════════════════════════════════════════════════════

/** Get list of available reciters */
export async function getReciters(): Promise<Reciter[]> {
  try {
    const recitations = await quranClient.resources.findAllRecitations();
    return recitations.map((r) => ({
      id: String(r.id),
      name: r.reciterName ?? r.translatedName?.name ?? 'Unknown',
      style: r.style ?? undefined,
    }));
  } catch (error) {
    console.error('Error fetching reciters:', error);
    return [];
  }
}

/** Get available translations */
export async function getTranslations(): Promise<TranslationSource[]> {
  try {
    const translations = await quranClient.resources.findAllTranslations();
    return translations.map((t) => ({
      id: t.id ?? 0,
      name: t.name ?? '',
      authorName: t.authorName ?? '',
      slug: t.slug,
      languageName: t.languageName ?? '',
    }));
  } catch (error) {
    console.error('Error fetching translations:', error);
    return [];
  }
}

/** Get translation info/details */
export async function getTranslationInfo(translationId: string): Promise<{ id: number; info: string } | null> {
  try {
    const info = await quranClient.resources.findTranslationInfo(translationId);
    return { id: info.id ?? 0, info: info.info ?? '' };
  } catch (error) {
    console.error(`Error fetching translation info ${translationId}:`, error);
    return null;
  }
}

/** Get available tafsirs */
/** Get available tafsirs */
export async function getTafsirs(language?: string): Promise<TafsirSource[]> {
  try {
    const tafsirs = await quranClient.resources.findAllTafsirs(
      language ? { language: language as any } : undefined
    );
    
    console.log(`[DEBUG getTafsirs] raw count=${tafsirs.length} language=${language}`, tafsirs[0]);

    return tafsirs.map((t) => ({
      id: t.id ?? 0,
      name: t.name ?? '',
      authorName: t.authorName ?? '',
      slug: t.slug,
      languageName: t.languageName ?? '',
    }));
  } catch (error) {
    console.error('Error fetching tafsirs:', error);
    return [];
  }
}

/** Get tafsir info/details */
export async function getTafsirInfo(tafsirId: string): Promise<{ id: number; info: string } | null> {
  try {
    const info = await quranClient.resources.findTafsirInfo(tafsirId);
    return { id: info.id ?? 0, info: info.info ?? '' };
  } catch (error) {
    console.error(`Error fetching tafsir info ${tafsirId}:`, error);
    return null;
  }
}

/** Get all supported languages */
export async function getLanguages(): Promise<LanguageInfo[]> {
  try {
    const languages = await quranClient.resources.findAllLanguages();
    return languages.map((l) => ({
      id: l.id ?? 0,
      name: l.name ?? '',
      nativeName: l.nativeName ?? '',
      isoCode: l.isoCode ?? '',
      direction: l.direction ?? 'ltr',
    }));
  } catch (error) {
    console.error('Error fetching languages:', error);
    return [];
  }
}

/** Get chapter reciters (full chapter audio reciters) */
export async function getChapterReciters(): Promise<{ id: number; name: string; arabicName?: string }[]> {
  try {
    const reciters = await quranClient.resources.findAllChapterReciters();
    return reciters.map((r) => ({
      id: r.id,
      name: r.name,
      arabicName: r.arabicName,
    }));
  } catch (error) {
    console.error('Error fetching chapter reciters:', error);
    return [];
  }
}

/** Get recitation styles (mujawwad, murattal, muallim) */
export async function getRecitationStyles(): Promise<{ mujawwad: string; murattal: string; muallim: string } | null> {
  try {
    const styles = await quranClient.resources.findAllRecitationStyles();
    return {
      mujawwad: styles.mujawwad,
      murattal: styles.murattal,
      muallim: styles.muallim,
    };
  } catch (error) {
    console.error('Error fetching recitation styles:', error);
    return null;
  }
}

// ═══════════════════════════════════════════════════════════
// QURANENC API (thai_mokhtasar translation)
// ═══════════════════════════════════════════════════════════

export interface QuranEncAya {
  sura: string;
  aya: string;
  translation: string;
  footnotes: string;
}

const quranEncCache = new Map<number, QuranEncAya[]>();

/** Fetch full surah translation from QuranEnc.com (thai_mokhtasar) */
export async function getQuranEncTranslation(surahNumber: number): Promise<QuranEncAya[]> {
  if (quranEncCache.has(surahNumber)) {
    return quranEncCache.get(surahNumber)!;
  }
  try {
    const res = await fetch(
      `https://quranenc.com/api/v1/translation/sura/thai_mokhtasar/${surahNumber}`
    );
    if (!res.ok) throw new Error(`QuranEnc API error: ${res.status}`);
    const json = await res.json();
    const result: QuranEncAya[] = json.result ?? json ?? [];
    quranEncCache.set(surahNumber, result);
    return result;
  } catch (error) {
    console.error(`Error fetching QuranEnc translation for surah ${surahNumber}:`, error);
    return [];
  }
}

// ═══════════════════════════════════════════════════════════
// LOCAL STORAGE (reading progress, settings, cache)
// ═══════════════════════════════════════════════════════════

export function saveReadingProgress(page: number, surahNumber?: number, ayahNumber?: number): void {
  try {
    const progress: ReadingProgress = {
      currentPage: page,
      surahNumber,
      ayahNumber,
      lastReadAt: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY_PROGRESS, JSON.stringify(progress));
  } catch (error) {
    console.error('Error saving reading progress:', error);
  }
}

export function getReadingProgress(): ReadingProgress | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PROGRESS);
    if (stored) return JSON.parse(stored);
  } catch (error) {
    console.error('Error getting reading progress:', error);
  }
  return null;
}

export function saveReaderSettings(settings: ReaderSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY_SETTINGS, JSON.stringify(settings));
  } catch (error) {
    console.error('Error saving reader settings:', error);
  }
}

export function getReaderSettings(): ReaderSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_SETTINGS);
    if (stored) return JSON.parse(stored);
  } catch (error) {
    console.error('Error getting reader settings:', error);
  }
  return {
    showTranslation: false,
    translationLanguage: 'th',
    fontSize: 'small',
    selectedReciter: '7',
    selectedTranslation: 51, // Thai Translation (King Fahad Quran Complex)
    showTajweed: true,
    tadabburTranslationSource: 'qf',
  };
}

export function clearCache(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_CACHE);
  } catch (error) {
    console.error('Error clearing cache:', error);
  }
}
